import { useEffect, useRef, type ReactElement } from "react";
import { RouterProvider } from "@tanstack/react-router";
import { authStore } from "./lib/auth";
import { useAuth } from "./lib/auth/use-auth";
import { sanitizeReturnPath } from "./lib/auth/returnPath";
import { AppShell, OfflineBanner } from "./shell";
import { AuthLayout } from "./features/auth/AuthLayout";
import { SignInCard } from "./features/auth/SignInCard";
import { CallbackScreen } from "./features/auth/CallbackScreen";
import { router } from "./router";
import { pushEvent, trackPageViews } from "./lib/analytics";

export function AppRoot(): ReactElement {
  const { status } = useAuth();

  useEffect(() => trackPageViews(router), []);

  const authScreenTracked = useRef(false);
  useEffect(() => {
    if (status !== "unauthenticated" || authScreenTracked.current) return;
    authScreenTracked.current = true;
    pushEvent("auth_screen_view");
  }, [status]);

  let content: ReactElement;
  if (status === "authenticated") {
    content = <RouterProvider router={router} />;
  } else if (status === "authenticating") {
    content = <CallbackScreen />;
  } else {
    content = (
      <AppShell bare currentPath="/app">
        <AuthLayout>
          <SignInCard
            onSignIn={() => {
              pushEvent("auth_start");
              const returnTo = sanitizeReturnPath(
                window.location.pathname +
                  window.location.search +
                  window.location.hash,
                import.meta.env.BASE_URL,
                window.location.origin,
              );
              authStore.beginLogin(returnTo ? { returnTo } : {});
            }}
          />
        </AuthLayout>
      </AppShell>
    );
  }

  return (
    <>
      <OfflineBanner />
      {content}
    </>
  );
}
